import type { FC } from 'react';
import { BynderFormats, getImageUrl } from '../../../../utils/bynder-utils';

export type ImageLoadingType = 'eager' | 'lazy' | undefined;

export type ImageFetchPriorityType = 'high' | 'low' | 'auto' | undefined;

export interface ImageProps {
    loading?: ImageLoadingType;
    fetchPriority?: ImageFetchPriorityType;
    onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

interface ProductImageProps extends ImageProps {
    id: string;
    name: string;
    imageUrl: string;
}

export const ProductImage: FC<ProductImageProps> = ({
    id,
    name,
    imageUrl,
    loading,
    fetchPriority,
    onLoad,
}) => {
    const mediumUrl: string = getImageUrl(imageUrl, BynderFormats.WEBP_MEDIUM) || '';

    // The combination of display: block, height: auto, and defined width/max-width ensures:
    // 1. display: block - Makes the image a block element that takes full width
    // 2. height: auto - Maintains aspect ratio as width changes
    // 3. width/max-width - Controls the image size while preventing layout shifts
    // 4. aspect-square - Forces 1:1 aspect ratio
    // Together these prevent content layout shifts (CLS) while maintaining proper image proportions
    return (
        <figure className="group relative">
            <img
                data-testid={`product-image-${id}`}
                src={mediumUrl}
                alt={name}
                width="500"
                height="500"
                className="mt-[1px] block aspect-square h-auto w-full max-w-[500px]"
                loading={loading}
                fetchpriority={fetchPriority}
                onLoad={onLoad}
            />
            <figcaption id={`caption-product-image-${id}`} className="sr-only">
                {name}
            </figcaption>
        </figure>
    );
};

ProductImage.defaultProps = {
    loading: undefined,
    fetchPriority: undefined,
};
